<ng-template #expandHeader>
  <div class="task-table-cell task-table-expand-cell">
    <!-- A miniature subtask chip says what toggles; chevrons point apart to expand and together to collapse. -->
    <proffeo-ui-button
      (clicked)="toggleAllRows()"
      [tooltip]="subtasksToggleLabelKey() | translate"
      [ariaLabel]="subtasksToggleLabelKey() | translate"
      variant="ghost"
      size="xs"
    >
      <span
        proffeoUiButtonIcon
        aria-hidden="true"
        class="task-table-toggle-glyph"
      >
        <svg
          viewBox="0 0 24 6"
          class="task-table-toggle-arrows"
        >
          @if (hasExpandedRows()) {
            <path d="M8.5 0.75 12 3.75l3.5-3" />
          } @else {
            <path d="M8.5 5.25 12 2.25l3.5 3" />
          }
        </svg>
        <span
          [class.proffeo-ui-tree-count-chip-active]="hasExpandedRows()"
          class="proffeo-ui-tree-count-chip task-table-toggle-chip"
        >
          <mat-icon
            [class.opacity-50]="!hasExpandedRows()"
            fontIcon="subdirectory_arrow_right"
            class="icon-sm"
          />
          <span>1</span>
        </span>
        <svg
          viewBox="0 0 24 6"
          class="task-table-toggle-arrows"
        >
          @if (hasExpandedRows()) {
            <path d="M8.5 5.25 12 2.25l3.5 3" />
          } @else {
            <path d="M8.5 0.75 12 3.75l3.5-3" />
          }
        </svg>
      </span>
    </proffeo-ui-button>
  </div>
</ng-template>

<ng-template
  #expandCell
  let-row
>
  <div class="task-table-cell task-table-expand-cell">
    @if (row.hasChildren) {
      <button
        (click)="toggleExpanded(row.task, $event)"
        [attr.aria-expanded]="row.expanded"
        [attr.aria-label]="'project-tasks.list.task.toggle-subtasks' | translate"
        [class.proffeo-ui-tree-count-chip-active]="row.expanded"
        type="button"
        class="proffeo-ui-tree-count-chip"
      >
        <mat-icon
          fontIcon="subdirectory_arrow_right"
          class="icon-sm opacity-50"
        />
        <span>{{ row.task.subtasksCount }}</span>
      </button>
    } @else if (row.depth > 0) {
      <mat-icon
        fontIcon="subdirectory_arrow_right"
        class="icon-sm opacity-50"
      />
    }
  </div>
</ng-template>

<ng-template #avatarHeader>
  <div class="task-table-cell task-table-avatar-header">
    <span class="task-table-avatar-header-label">{{ avatarColumnName() }}</span>
    <button
      (click)="hideAvatarColumn($event)"
      [attr.aria-label]="avatarHideLabel()"
      [matTooltip]="avatarHideLabel()"
      type="button"
      class="task-table-avatar-hide"
    >
      <mat-icon
        fontIcon="visibility_off"
        aria-hidden="true"
        class="icon-sm"
      />
    </button>
  </div>
</ng-template>

<ng-template
  #avatarCell
  let-row
>
  <div class="task-table-cell task-table-avatar-cell">
    @if (row.task.getAvatarUrl()(); as avatarUrl) {
      <img
        [src]="avatarUrl"
        alt=""
        class="task-table-avatar"
      />
    }
  </div>
</ng-template>

<ng-template
  #numberCell
  let-row
>
  <div class="task-table-cell">
    <div data-proffeo-ignore-row-click>
      <proffeo-task-type-select
        [readonly]="true"
        [activeTaskType]="row.task.taskType()"
        [taskTypes]="taskTypes()"
        [taskNumber]="row.task.taskNumber"
      />
    </div>
  </div>
</ng-template>

<ng-template
  #nameCell
  let-row
>
  <div
    [style]="indentStyle(row.depth)"
    class="task-table-name-cell"
  >
    <span
      [matTooltip]="row.task.name"
      class="task-table-name-text"
    >
      {{ row.task.name }}
    </span>
  </div>
</ng-template>

<ng-template
  #tagsCell
  let-row
>
  <div class="task-table-cell task-table-tags-cell">
    <proffeo-task-table-tags [tags]="tagsForTask(row.task.id)" />
  </div>
</ng-template>

<ng-template
  #statusCell
  let-row
>
  <div class="task-table-cell">
    <div data-proffeo-ignore-row-click>
      <proffeo-task-status-select
        (statusSelected)="statusChanged.emit({ status: $event, task: row.task })"
        [mode]="TaskStatusSelectEnum.DOTS"
        [taskStatus]="row.task.status"
        [small]="true"
        [readonly]="false"
      />
    </div>
  </div>
</ng-template>

<ng-template
  #priorityCell
  let-row
>
  <div class="task-table-cell">
    @if (row.task.priority !== TaskPriority.NORMAL) {
      <div data-proffeo-ignore-row-click>
        <proffeo-task-priority-select
          (prioritySelected)="priorityChanged.emit({ priority: $event, task: row.task })"
          [taskPriority]="row.task.priority"
          [readonly]="false"
          [small]="true"
        />
      </div>
    }
  </div>
</ng-template>

<ng-template
  #assigneesCell
  let-row
>
  <div class="task-table-cell">
    <div data-proffeo-ignore-row-click>
      <proffeo-ui-task-assignees
        (changeIndustry)="industryClicked.emit(row.task)"
        (changeGroup)="changeGroupClicked.emit(row.task)"
        (changeUsers)="changeUsersClicked.emit(row.task)"
        [projectIndustry]="row.task.projectIndustry()"
        [projectGroupAdmin]="row.task.projectGroupAdministrator()"
        [taskUsers]="taskUsers(row.task.id)"
        [avatarsOverlapping]="true"
        [avatarsLimit]="2"
      />
    </div>
  </div>
</ng-template>

<ng-template
  #industryCell
  let-row
>
  <div class="task-table-cell">
    <div data-proffeo-ignore-row-click>
      <proffeo-task-industry-select
        (industryClicked)="industryClicked.emit(row.task)"
        [projectIndustry]="row.task.projectIndustry()"
        [isReadonly]="true"
        [isSmall]="true"
        [showIcon]="false"
      />
    </div>
  </div>
</ng-template>

<ng-template
  #locationCell
  let-row
>
  <div class="task-table-cell min-w-0">
    <div
      data-proffeo-ignore-row-click
      class="min-w-0 w-full"
    >
      <proffeo-location-select
        (locationClicked)="locationClicked.emit(row.task)"
        [isReadonly]="true"
        [location]="row.task.location()"
        [showIcon]="false"
        [maxLines]="2"
        class="min-w-0 w-full"
      />
    </div>
  </div>
</ng-template>

<ng-template
  #timerCell
  let-row
>
  <div class="task-table-cell">
    <proffeo-task-timer
      [mode]="taskTimerMode.readonly"
      [summaryTime]="$safeNavigationMigration(row.task.statistics?.summaryTime)"
      [estimation]="$safeNavigationMigration(row.task.estimation?.time)"
    />
  </div>
</ng-template>

<ng-template
  #progressCell
  let-row
>
  <span class="task-table-progress">{{ row.task.percentageOfProgress ?? 0 }} %</span>
</ng-template>

<ng-template
  #startDateCell
  let-row
>
  <div class="task-table-cell">
    <div data-proffeo-ignore-row-click>
      <proffeo-ui-date-picker-compact
        (valueChange)="emitStartDate($event, row.task)"
        [max]="row.task.endDate"
        [readonly]="timelineEnabled()"
        [value]="row.task.startDate"
        [iconVisible]="false"
        dateFormat="shortDate"
        placeholderKey="project-tasks.list.column-headers.start-date"
        size="sm"
      />
    </div>
  </div>
</ng-template>

<ng-template
  #endDateCell
  let-row
>
  <div class="task-table-cell">
    <div data-proffeo-ignore-row-click>
      <proffeo-ui-date-picker-compact
        (valueChange)="emitEndDate($event, row.task)"
        [min]="row.task.startDate"
        [readonly]="timelineEnabled()"
        [value]="row.task.endDate"
        [iconVisible]="false"
        dateFormat="shortDate"
        placeholderKey="project-tasks.list.column-headers.end-date"
        size="sm"
      />
    </div>
  </div>
</ng-template>

<ng-template
  #actionsCell
  let-row
>
  <div class="task-table-cell task-table-actions-cell">
    <div data-proffeo-ignore-row-click>
      <proffeo-task-row-actions-menu [task]="row.task" />
    </div>
  </div>
</ng-template>

<ng-template #timelineHeader>
  <proffeo-ui-timeline
    [todayDay]="todayDay()"
    [fromDay]="period().from"
    [toDay]="period().to"
    [locale]="locale()"
    [label]="'task-schedule.axis' | translate"
    mode="axis"
  />
</ng-template>
<ng-template
  #timelineCell
  let-row
>
  <proffeo-ui-timeline
    [todayDay]="todayDay()"
    [fromDay]="period().from"
    [toDay]="period().to"
    [startDay]="row.task.startDate ? calendarDay(row.task.startDate) : null"
    [endDay]="row.task.endDate ? calendarDay(row.task.endDate) : null"
    [emptyLabel]="rangeLabel(row.task)"
    [label]="row.task.name + ': ' + rangeTooltip(row.task)"
    [matTooltip]="rangeTooltip(row.task)"
  />
</ng-template>
@for (preset of [tableId()]; track preset) {
  <proffeo-ui-table
    (groupByColumnIdChange)="onGroupColumnChanged($event)"
    (expandedGroupIdsChange)="onGroupExpansionChanged($event)"
    (rowClick)="onRowClick($event)"
    (sortChange)="onSortChange($event)"
    [externalGrouping]="!!taskFilters"
    [groupByColumnId]="groupColumnId()"
    [expandedGroupIds]="$safeNavigationMigration(taskFilters?.expandedTaskGroupIds())"
    [columns]="columns()"
    [compactLayout]="
      timelineEnabled()
        ? null
        : { belowWidthPx: 800, hiddenColumnIds: ['avatar'], stickyColumnId: 'name', minWidthChars: 20 }
    "
    [rowId]="resolveRowId"
    [rows]="tableRows()"
    [selectedRows]="selectedTableRows()"
    [sort]="tableSort()"
    [tableId]="tableId()"
    [resizable]="true"
    [showColumnManagerTrigger]="false"
    [emptyText]="'project-tasks.list.no-tasks' | translate"
    [tableAriaLabel]="'project-tasks.list.table-aria-label' | translate"
    orderMode="external-sort"
    class="task-table"
  />
}
